import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import MultiSelectDropdown from 'components/ReusableComponents/MultiSelectDropdown'
import Table, { TitleCell, TitleRow } from 'components/ReusableComponents/Table'
import Tab from 'components/ReusableComponents/Tabs'
import useSyncedState from 'hooks/useSyncedState'
import { parentChainNames } from 'main/hotelBrandsAndChains'
import ReactTooltip from 'react-tooltip'
import canadianProvinces from 'static/addresses/canadianProvinces'
import usStates from 'static/addresses/usStates'

import { HotelCard } from './HotelCard/HotelCard'
import styles from './Hotels.module.scss'
import { useHotelsData } from './useData'

const sortableIndexes = [
  'hotel',
  'group',
  'brand',
  'addedOn',
  'stateTimeZone',
  'customerSuccessManager'
]

const HotelsByCsm = () => {
  const [type] = useSyncedState('type', 'hotelsType', 'onboarding')

  const {
    sortedAndFilteredHotels,
    hotels,
    customerSuccessManagers,
    getCustomerSuccessManagers,
    isLoading,
    sort: { sortParam, setSortParam, sortDirection, setSortDirection },
    filter: {
      selectedGroup,
      setSelectedGroup,
      selectedParentChain,
      setSelectedParentChain,
      selectedState,
      setSelectedState,
      selectedTags,
      setSelectedTags,
      selectedCustomerSuccessManagers,
      setSelectedCustomerSuccessManagers,
      clearFilters
    },
    search: { searchQuery, setSearchQuery }
  } = useHotelsData(type)

  function handleTitleClick(i) {
    if (sortParam === sortableIndexes[i]) {
      setSortDirection(d => (d === 'up' ? 'down' : 'up'))
    }

    if (sortableIndexes[i]) {
      setSortParam(sortableIndexes[i])
    }
  }

  const sortIndex = sortableIndexes.indexOf(sortParam)

  return (
    <div className={styles.wrapper}>
      <ReactTooltip id="hotels-by-csm-tooltip" place="top" type="dark" effect="solid" />

      <CustomHeader
        title={`Manage Customer Success Managers (${
          sortedAndFilteredHotels?.length ?? 0
        }) `}
        isFixed
        width={1100}
      />

      <Tab.Parent>
        <Tab exact to="/hotels-by-csm?type=allActive">
          Active {type === 'allActive' && !isLoading && ` (${hotels.length})`}
        </Tab>
        <Tab exact to="/hotels-by-csm?type=churned">
          Churned
          {type === 'churned' && !isLoading && ` (${hotels.length})`}
        </Tab>
        <Tab exact to="/hotels-by-csm?type=paused">
          Paused
          {type === 'paused' && !isLoading && ` (${hotels.length})`}
        </Tab>
      </Tab.Parent>

      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <>
          <Table
            widths={[280, 280, 320, 100, 30]}
            sortIndex={sortIndex}
            sortDirection={sortDirection}
            handleTitleCellClick={handleTitleClick}
          >
            <div className={styles.headerWrapper}>
              <div className={styles.header}>
                <Dropdown
                  className={styles.dropdown}
                  options={{
                    all: ' All groups',
                    ...(selectedGroup === 'all'
                      ? {}
                      : { [selectedGroup]: selectedGroup }),
                    ...hotels.reduce((prev, curr) => {
                      return {
                        ...prev,
                        [curr.merchantName]:
                          curr.merchantName === 'Independently Managed Hotels'
                            ? '- Independently Managed'
                            : curr.merchantName
                      }
                    }, {})
                  }}
                  value={selectedGroup}
                  onChange={e => setSelectedGroup(e.target.value)}
                />
                <Dropdown
                  className={styles.dropdown}
                  options={{
                    all: ' All brands',
                    ...(selectedParentChain === 'all'
                      ? {}
                      : { [selectedParentChain]: parentChainNames[selectedParentChain] }),
                    ...hotels.reduce(
                      (prev, curr) => ({
                        ...prev,
                        [curr.parentChainCode]:
                          parentChainNames[curr.parentChainCode] ||
                          curr.parentChainCode ||
                          '-'
                      }),
                      {}
                    )
                  }}
                  value={selectedParentChain}
                  onChange={e => setSelectedParentChain(e.target.value)}
                />
                <Dropdown
                  className={styles.dropdown}
                  options={{
                    all: ' All States',
                    ...(selectedState === 'all'
                      ? {}
                      : {
                          [selectedState]: selectedState
                        }),
                    ...hotels.reduce(
                      (prev, curr) => ({
                        ...prev,
                        [curr.stateCode]:
                          { ...usStates, ...canadianProvinces }?.[curr.stateCode] ||
                          curr.stateCode ||
                          '-'
                      }),
                      {}
                    )
                  }}
                  value={selectedState}
                  onChange={e => setSelectedState(e.target.value)}
                />
                <MultiSelectDropdown
                  options={hotels
                    .flatMap(h => h.csmTags)
                    .reduce(
                      (prev, curr) => ({
                        ...prev,
                        [curr.name]: curr.name
                      }),
                      {}
                    )}
                  value={selectedTags}
                  itemName="CSM Tags"
                  onChangeValue={setSelectedTags}
                  className={styles.statusFilter}
                  hasUnselectButton
                />
                <MultiSelectDropdown
                  options={customerSuccessManagers.reduce(
                    (prev, curr) => ({
                      ...prev,
                      [curr.id]: curr.fullName
                    }),
                    {
                      unassigned: 'Unassigned'
                    }
                  )}
                  value={selectedCustomerSuccessManagers}
                  itemName="CSM's"
                  onChangeValue={setSelectedCustomerSuccessManagers}
                  className={styles.statusFilter}
                  hasUnselectButton
                />
                <input
                  type="search"
                  className={styles.searchFilter}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Type anything to filter"
                />
                <button onClick={() => clearFilters()} className={styles.clearFilters}>
                  Clear filters{' '}
                </button>
              </div>
              <TitleRow>
                <TitleCell>HOTEL</TitleCell>
                <TitleCell>GROUP</TitleCell>
                <TitleCell>CSM TAGS</TitleCell>
                <TitleCell>CUSTOMER SUCCESS MANAGER</TitleCell>
                <TitleCell></TitleCell>
              </TitleRow>
            </div>

            {sortedAndFilteredHotels.map(hotel => (
              <HotelCard
                key={hotel.id}
                hotel={hotel}
                csmList={customerSuccessManagers}
                getCustomerSuccessManagers={getCustomerSuccessManagers}
              />
            ))}
          </Table>
        </>
      )}
    </div>
  )
}

export default HotelsByCsm
